import React from "react";
import { cn } from "../../lib/utils";
import { renderCustomComponent } from "./RenderCustomComponent";
import { SectionRenderer } from "./display";
import { ConfiguratorConfig } from "../../hooks/configurator/types";
import { ConfigurationSummary } from "./ConfigurationSummary";

interface ConfigurationPanelProps {
  config: ConfiguratorConfig;
  fullScreen: boolean;
  totalPrice: number;
  /** Whether this panel is used in the mobile/tablet split layout (not desktop sidebar). */
  isMobileDrawer?: boolean;
}

function SectionBlocks({
  config,
  isMobileDrawer,
}: {
  config: ConfiguratorConfig;
  isMobileDrawer: boolean;
}) {
  return (
    <>
      {config.sections.map((section) => (
        <div key={section.id} className="w-full">
          <div
            id={section.id}
            className={cn(
              "flex flex-col items-center top-0 z-10 py-3 md:py-6",
              isMobileDrawer ? "px-0" : "-mx-4 px-4 lg:-mx-6 lg:px-6",
            )}
          >
            <div className="w-full">
              <div className="mb-6 pb-4 border-b border-gray-100">
                <h3
                  className="text-lg md:text-xl text-black mb-2"
                  style={{ fontWeight: "300" }}
                >
                  {section.label}
                </h3>
              </div>

              {section?.metadata?.customComponentName &&
                renderCustomComponent(section.metadata.customComponentName, {
                  section,
                })}

              {!section?.metadata?.customComponentName && (
                <SectionRenderer section={section} />
              )}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

export const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
  config,
  fullScreen,
  totalPrice,
  isMobileDrawer = false,
}) => {
  if (fullScreen) return null;

  if (!isMobileDrawer) {
    return (
      <div
        className={cn(
          "flex flex-col gap-0 items-start justify-start overflow-y-auto",
          "basis-4/12 lg:basis-3/12 landscape:lg:basis-3/12 xl:basis-3/12 2xl:basis-2/12 2xl:max-w-sm shrink-0 py-6 pt-0 px-4 lg:px-6 bg-gray-50 border-l border-gray-200",
        )}
      >
        <SectionBlocks config={config} isMobileDrawer={false} />
        <ConfigurationSummary totalPrice={totalPrice} />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden border-t border-gray-200 bg-gray-50">
      <div
        className={cn(
          "min-h-0 w-full flex-1 overflow-y-auto px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:px-6",
          "space-y-1 transition-all duration-300 ease-in-out",
        )}
      >
        <SectionBlocks config={config} isMobileDrawer />
        <ConfigurationSummary totalPrice={totalPrice} />
      </div>
    </div>
  );
};
